/**
 * Copilot Agent store — bridges the globally-mounted GrcCopilotSidebar (Agent
 * mode + "Créer avec l'IA") with the CrudPage that is currently rendered.
 *
 * The copilot sidebar / command-bar live OUTSIDE the routed page tree, so they
 * cannot reach a CrudPage's local create-dialog state directly. Each CrudPage
 * therefore REGISTERS a lightweight "create surface" descriptor while it is
 * mounted (its module slug, field schema, create endpoint, and imperative
 * open / prefill callbacks). The agent executor + smart-create then drive that
 * surface — opening the create dialog pre-filled, or POSTing a new record.
 *
 * `pendingCreate` is a one-shot request that survives a route change: smart-
 * create / the agent set it, navigate to the target route, and the CrudPage
 * consumes it on mount (open dialog + AI pre-fill).
 */

import { create } from 'zustand'

/** Field schema shared with the AI form-fillers (keyed by field name). */
export interface CopilotCreateField {
  name: string
  label: string
  type?: string
  required?: boolean
  options?: string[]
}

/** Imperative handle a mounted CrudPage exposes to the copilot layer. */
export interface CopilotCreateSurface {
  /** First route segment, e.g. `dashboard-pro`. */
  module: string
  /** Full pathname the surface was registered for (uniqueness key). */
  routePath: string
  /** Resolved indexed entity type (or module slug fallback). */
  entityType: string
  /** FR page title (for labels / planner grounding). */
  title: string
  /** Create-form field schema. */
  fields: CopilotCreateField[]
  /** Backend create endpoint (relative path) when the page wires one. */
  createEndpoint?: string
  /** Open the create dialog, optionally pre-filled with `prefill` values. */
  open: (prefill?: Record<string, unknown>) => void
  /** Merge AI-produced values into the current create draft. */
  applyPrefill: (values: Record<string, unknown>) => void
}

/** One-shot "open this module's create dialog pre-filled" request. */
export interface PendingCreate {
  module: string
  /** Direct values to seed the draft with (optional). */
  prefill?: Record<string, unknown>
  /** NL prompt → the CrudPage runs /ai/copilot/fill-form after opening. */
  prompt?: string
}

interface CopilotAgentState {
  surface: CopilotCreateSurface | null
  registerSurface: (s: CopilotCreateSurface) => void
  /** Only clears when the registered surface still matches `routePath`. */
  unregisterSurface: (routePath: string) => void

  pendingCreate: PendingCreate | null
  requestCreate: (p: PendingCreate) => void
  clearPendingCreate: () => void
}

export const useCopilotAgentStore = create<CopilotAgentState>((set) => ({
  surface: null,
  registerSurface: (s) => set({ surface: s }),
  unregisterSurface: (routePath) =>
    set((st) => (st.surface?.routePath === routePath ? { surface: null } : st)),

  pendingCreate: null,
  requestCreate: (p) => set({ pendingCreate: p }),
  clearPendingCreate: () => set({ pendingCreate: null }),
}))
